import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

export interface SplitOptions {
  /** `y` resizes a height and drags vertically; `x` resizes a width. */
  axis: 'x' | 'y';
  /**
   * True when the panel being sized is *after* the handle, so dragging towards
   * the start of the axis makes it bigger - a right-hand column, typically.
   */
  invert?: boolean;
  /** Below this the panel stops being useful. */
  min?: number;
  /** Left for everything on the other side of the handle. */
  minRemaining?: number;
}

/** Arrow keys move by this much, so a split is reachable without a mouse. */
const STEP = 16;

/**
 * A pane size the user can drag, remembered between sessions.
 *
 * How much room a panel deserves is not something a stylesheet can decide: it
 * depends on the screen, and on what the director is doing at the time. So it
 * is theirs to set, and it survives a reload - a size you have to re-drag every
 * session is barely better than a fixed one.
 *
 * Pointer capture rather than window listeners: the handle keeps receiving
 * moves even when the cursor outruns it, which it will, and there is nothing
 * to unsubscribe if the component goes away mid-drag.
 */
export function useSplitSize(storageKey: string, fallback: number, options: SplitOptions) {
  const { axis, invert = false, min = 110, minRemaining = 220 } = options;

  const clamp = useCallback(
    (value: number) => {
      const available = axis === 'y' ? window.innerHeight : window.innerWidth;
      const max = Math.max(min, available - minRemaining);
      return Math.min(max, Math.max(min, Math.round(value)));
    },
    [axis, min, minRemaining],
  );

  const [size, setSize] = useState(() => {
    const stored = Number(window.localStorage.getItem(storageKey));
    return clamp(Number.isFinite(stored) && stored > 0 ? stored : fallback);
  });

  const drag = useRef<{ origin: number; size: number } | null>(null);
  // The handlers are stable, so they read the live value from here rather than
  // closing over a size that would be stale by the next frame.
  const latest = useRef(size);

  const apply = useCallback(
    (value: number) => {
      const next = clamp(value);
      latest.current = next;
      setSize(next);
    },
    [clamp],
  );

  const remember = useCallback(() => {
    window.localStorage.setItem(storageKey, String(latest.current));
  }, [storageKey]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = {
        origin: axis === 'y' ? event.clientY : event.clientX,
        size: latest.current,
      };
    },
    [axis],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = drag.current;
      if (!start) return;
      const moved = (axis === 'y' ? event.clientY : event.clientX) - start.origin;
      apply(start.size + (invert ? -moved : moved));
    },
    [apply, axis, invert],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!drag.current) return;
      drag.current = null;
      event.currentTarget.releasePointerCapture(event.pointerId);
      remember();
    },
    [remember],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const grow = axis === 'y' ? 'ArrowDown' : 'ArrowRight';
      const shrink = axis === 'y' ? 'ArrowUp' : 'ArrowLeft';
      if (event.key !== grow && event.key !== shrink) return;
      event.preventDefault();
      const delta = event.key === grow ? STEP : -STEP;
      apply(latest.current + (invert ? -delta : delta));
      remember();
    },
    [apply, axis, invert, remember],
  );

  return {
    size,
    /** Spread onto the drag handle. */
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      className: axis === 'y' ? 'split-handle split-handle--y' : 'split-handle split-handle--x',
      role: 'separator' as const,
      'aria-orientation': (axis === 'y' ? 'horizontal' : 'vertical') as 'horizontal' | 'vertical',
      tabIndex: 0,
    },
  };
}
